import {EntityStoreService} from "../entity-store/entity-store.service";
import {catchError, firstValueFrom, map} from "rxjs";
import {EntityMetadata, Identifiable, QueryParam} from "../store-types.model";
import {PaginatedEntityApiService} from "./paginated-entity-api.service";
import {EntityFacadeService} from "../entity-store/entity-facade.service";
import {ReduxTrack} from "../utils/devtool.service";

/**
 * Service class for managing entities through the store and API.
 *
 * @template TEntity - The type of entities managed by the service.
 */
export class PaginatedEntityFacadeService<
  TEntity extends Identifiable,
  TApi extends PaginatedEntityApiService<TEntity>,
  TMetadata extends EntityMetadata = EntityMetadata,
  TStore extends EntityStoreService<TEntity> = EntityStoreService<
    TEntity,
    TMetadata
  >,
> extends EntityFacadeService<TEntity, TApi> {
  /**
   * Constructs a new PaginatedEntityFacadeService.
   *
   * @param {EntityStoreService<TEntity>} store - The entity store service.
   * @param {PaginatedEntityApiService<TEntity>} api - The entity API service.
   */
  constructor(
    protected api: TApi,
    store?: TStore,
  ) {
    super(api, store);
  }

  protected async loadPage(page: number, limit: number, query: QueryParam) {
    return await firstValueFrom(this.loadPageObservable(page, limit, query));
  }

  @ReduxTrack
  public loadPageObservable(page: number, limit: number, query: QueryParam) {
    this.store.incrementLoading();
    return this.api.loadPage(page, limit, query).pipe(
      map((result) => {
        this.store.addMany(result.data);
        this.store.decrementLoading();
        const ids = result.data.map((e) => e.id);

        return { ids, total: result.total };
      }),
      catchError((e) => {
        this.store.decrementLoading();
        throw e;
      }),
    );
  }
}
